import React, { useState, useEffect } from 'react'
import { formatDate } from '../../utils/api'
import { searchItems } from '../../utils/warehouseApi'

// The reconciliation view that pairs with the Putaway queue: every in-stock or
// draft item Prism has NO recorded shelf for, oldest first. Putaway shows only
// the last 14 days; this shows the whole backlog so long-standing gaps surface.
const REPORT_STATUSES = 'draft,in_stock'
const REPORT_LIMIT = 200

function statusBadge(shopifyStatus, quantity) {
  // Derived like PutawayResults: the response carries raw shopify_status, but
  // "In stock" means active AND quantity > 0.
  if (shopifyStatus === 'draft') return <span className="badge badge-info">Draft</span>
  if (shopifyStatus === 'active' && (quantity ?? 0) > 0) {
    return <span className="badge badge-success">In stock</span>
  }
  if (shopifyStatus === 'active') return <span className="badge badge-warning">Out of stock</span>
  if (shopifyStatus === 'archived') return <span className="badge badge-neutral">Archived</span>
  if (!shopifyStatus) return null
  return <span className="badge badge-neutral">{shopifyStatus}</span>
}

function ageInDays(createdAt) {
  if (!createdAt) return null
  const ms = Date.now() - new Date(createdAt).getTime()
  if (Number.isNaN(ms)) return null
  return Math.max(0, Math.floor(ms / 86400000))
}

function NoLocationReport() {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true); setError(null)
      try {
        const res = await searchItems({
          status: REPORT_STATUSES,
          has_location: false,
          sort: 'created_at_asc',
          limit: REPORT_LIMIT,
        })
        // Python /api/v1/inventory-items returns a bare array; tolerate { items: [] } too
        const list = Array.isArray(res) ? res : (Array.isArray(res?.items) ? res.items : [])
        if (!cancelled) setItems(list)
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  return (
    <div>
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <h2 style={{ margin: 0 }}>Stock with no location</h2>
        <p className="text-sm text-muted" style={{ marginTop: 4 }}>
          In-stock and draft items with no recorded shelf, oldest first. Use this to
          reconcile the floor: put each item away (Putaway) to record where it lives, or
          investigate if it has already sold.
        </p>
      </div>

      {loading ? (
        <div className="card" style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <div className="loading-spinner" />
        </div>
      ) : error ? (
        <div className="card" style={{ background: 'var(--color-error-light, #fee2e2)', padding: 16 }}>
          <strong>Error:</strong> {error}
        </div>
      ) : items.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-title">Nothing to reconcile</div>
            <div className="empty-state-description">
              Every in-stock and draft item has a recorded shelf location.
            </div>
          </div>
        </div>
      ) : (
        <div className="card">
          <table className="table">
            <thead>
              <tr>
                <th>Title</th>
                <th style={{ textAlign: 'center', width: 56 }}>Qty</th>
                <th>Status</th>
                <th>Item</th>
                <th>Shopify</th>
                <th>Registered</th>
                <th style={{ textAlign: 'right' }}>Age</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => {
                const age = ageInDays(item.created_at)
                return (
                  <tr key={item.item_id}>
                    <td style={{ fontWeight: 500, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
                      {item.title || '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>{item.quantity ?? '—'}</td>
                    <td>{statusBadge(item.shopify_status, item.quantity)}</td>
                    <td className="text-sm text-muted" style={{ fontFamily: 'monospace' }}>
                      {item.item_id || '—'}
                    </td>
                    <td className="text-sm text-muted" style={{ fontFamily: 'monospace' }}>
                      {item.shopify_product_id}
                    </td>
                    <td className="text-sm">{item.created_at ? formatDate(item.created_at) : '—'}</td>
                    <td className="text-sm" style={{ textAlign: 'right' }}>
                      {age === null ? '—' : `${age}d`}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {items.length >= REPORT_LIMIT && (
            <div className="text-sm text-muted" style={{ padding: 'var(--space-3)' }}>
              Showing the {REPORT_LIMIT} oldest. Reconcile these first, then reload for the rest.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default NoLocationReport
